import { Button, Dialog } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { fonts } from '../theme/fonts.theme';
import { DateStateProps } from './fourDatePicker';

export interface CustomFourDatePickerProps {
    openDialog: boolean;
    setOpenDialog: (value: boolean) => void;
    dateState: DateStateProps;
}

export const CustomFourDatePicker = ({
    openDialog,
    setOpenDialog,
    dateState,
}: CustomFourDatePickerProps) => {
    const now = new Date();
    // Temporary state for dates while dialog is open
    const [tempStart, setTempStart] = useState<Date>(dateState.currentDate.start);
    const [tempEnd, setTempEnd] = useState<Date>(dateState.currentDate.end);
    const [tempLastStart, setTempLastStart] = useState<Date | null>(
        dateState.lastDate ? dateState.lastDate.start : null
    );
    const [tempLastEnd, setTempLastEnd] = useState<Date | null>(
        dateState.lastDate ? dateState.lastDate.end : null
    );

    // Reset temporary state when dialog opens
    useEffect(() => {
        if (openDialog) {
            // Extract date components from UTC dates and create local dates for display
            // This ensures the DatePicker shows the correct date regardless of timezone
            const startDate = dateState.currentDate.start;
            const startLocal = new Date(
                startDate.getUTCFullYear(),
                startDate.getUTCMonth(),
                startDate.getUTCDate()
            );
            setTempStart(startLocal);

            const endDate = dateState.currentDate.end;
            const endLocal = new Date(
                endDate.getUTCFullYear(),
                endDate.getUTCMonth(),
                endDate.getUTCDate()
            );
            setTempEnd(endLocal);

            if (dateState.lastDate) {
                const lastStartDate = dateState.lastDate.start;
                const lastStartLocal = new Date(
                    lastStartDate.getUTCFullYear(),
                    lastStartDate.getUTCMonth(),
                    lastStartDate.getUTCDate()
                );
                setTempLastStart(lastStartLocal);

                const lastEndDate = dateState.lastDate.end;
                const lastEndLocal = new Date(
                    lastEndDate.getUTCFullYear(),
                    lastEndDate.getUTCMonth(),
                    lastEndDate.getUTCDate()
                );
                setTempLastEnd(lastEndLocal);
            }
        }
    }, [openDialog, dateState.currentDate.start, dateState.currentDate.end, dateState.lastDate]);

    const handleValidate = useCallback(() => {
        // Normalize start date to beginning of day in UTC (00:00:00.000 UTC)
        // Extract year, month, day from the selected date (in local timezone)
        const startYear = tempStart.getFullYear();
        const startMonth = tempStart.getMonth();
        const startDay = tempStart.getDate();
        const normalizedStart = new Date(Date.UTC(startYear, startMonth, startDay, 0, 0, 0, 0));

        // Normalize end date to end of day in UTC (23:59:59.999 UTC)
        const endYear = tempEnd.getFullYear();
        const endMonth = tempEnd.getMonth();
        const endDay = tempEnd.getDate();
        const normalizedEnd = new Date(Date.UTC(endYear, endMonth, endDay, 23, 59, 59, 999));

        // Apply the normalized dates to the actual state
        dateState.currentDate.setStart(normalizedStart);
        dateState.currentDate.setEnd(normalizedEnd);

        if (dateState.lastDate && tempLastStart && tempLastEnd) {
            // Normalize last date start to beginning of day in UTC
            const lastStartYear = tempLastStart.getFullYear();
            const lastStartMonth = tempLastStart.getMonth();
            const lastStartDay = tempLastStart.getDate();
            const normalizedLastStart = new Date(
                Date.UTC(lastStartYear, lastStartMonth, lastStartDay, 0, 0, 0, 0)
            );

            // Normalize last date end to end of day in UTC
            const lastEndYear = tempLastEnd.getFullYear();
            const lastEndMonth = tempLastEnd.getMonth();
            const lastEndDay = tempLastEnd.getDate();
            const normalizedLastEnd = new Date(
                Date.UTC(lastEndYear, lastEndMonth, lastEndDay, 23, 59, 59, 999)
            );

            dateState.lastDate.setStart(normalizedLastStart);
            dateState.lastDate.setEnd(normalizedLastEnd);
        }
        setOpenDialog(false);
    }, [tempStart, tempEnd, tempLastStart, tempLastEnd, dateState, setOpenDialog]);

    const handleClose = () => {
        // Reset temporary state when closing without validation
        setTempStart(dateState.currentDate.start);
        setTempEnd(dateState.currentDate.end);
        if (dateState.lastDate) {
            setTempLastStart(dateState.lastDate.start);
            setTempLastEnd(dateState.lastDate.end);
        }
        setOpenDialog(false);
    };

    // Handle Enter key to validate
    useEffect(() => {
        if (!openDialog) {
            return undefined;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
                event.preventDefault();
                event.stopPropagation();
                // Small delay to let any DatePicker animations complete
                setTimeout(() => {
                    handleValidate();
                }, 100);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [openDialog, handleValidate]);

    return (
        <Dialog open={openDialog} onClose={handleClose} sx={fonts.roboto}>
            <p style={{ textAlign: 'right' }}>
                <Button onClick={handleClose} sx={{ color: 'black', marginTop: 2 }}>
                    X
                </Button>
            </p>
            <div style={{ padding: 20 }}>
                <h2 style={{ textAlign: 'center' }}>Choisir des dates customisées</h2>
                <h3 style={{ paddingTop: 20 }}>Dates de l'analyse</h3>
                <div style={{ display: 'flex', flexDirection: 'row', paddingTop: 20 }}>
                    <div>
                        <p>Début</p>
                        <DatePicker
                            format="dd/MM/yyyy"
                            value={DateTime.fromJSDate(tempStart)}
                            onChange={(e) => {
                                setTempStart(e ? e.toJSDate() : now);
                            }}
                        />
                    </div>
                    <div>
                        <p>Fin</p>
                        <DatePicker
                            format="dd/MM/yyyy"
                            value={DateTime.fromJSDate(tempEnd)}
                            onChange={(e) => {
                                setTempEnd(e ? e.toJSDate() : now);
                            }}
                        />
                    </div>
                </div>
                {dateState.lastDate ? (
                    <>
                        <h3 style={{ paddingTop: 20 }}>Dates de la comparaison</h3>
                        <div style={{ display: 'flex', flexDirection: 'row', paddingTop: 20 }}>
                            <div>
                                <p>Début</p>
                                <DatePicker
                                    format="dd/MM/yyyy"
                                    value={
                                        tempLastStart ? DateTime.fromJSDate(tempLastStart) : null
                                    }
                                    onChange={(e) => {
                                        setTempLastStart(e ? e.toJSDate() : null);
                                    }}
                                />
                            </div>
                            <div>
                                <p>Fin</p>
                                <DatePicker
                                    format="dd/MM/yyyy"
                                    value={tempLastEnd ? DateTime.fromJSDate(tempLastEnd) : null}
                                    onChange={(e) => {
                                        setTempLastEnd(e ? e.toJSDate() : null);
                                    }}
                                />
                            </div>
                        </div>
                    </>
                ) : null}
                <div
                    style={{
                        paddingTop: 20,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                    }}
                >
                    <Button onClick={handleValidate}>Valider</Button>
                </div>
            </div>
        </Dialog>
    );
};
